/*
 * Copyright 2025 Intuition Robotics ltd
 */
import __asset_0 from "@res/icons/icon_arrowhead_dropdown_dark.svg";
import {TS_Checkbox} from "@intuitionrobotics/thunderstorm/frontend";
import * as React from "react";
import {useState} from "react";
import {COLORS} from "@res/colors";
import {ICONS} from "@res/icons";

const icon_arrow_up = __asset_0;

export const InnerNode = ({checked}: { checked: boolean }) => {
    return (
        <div className={"flex_center checkbox"}>
            {checked && <div className={"flex_center inner"}/>}
        </div>);
};

export type MultiSelectOption<T extends any = string> = {
    label: string,
    value: T,
    checked: boolean,
    count?: number
}

type Props<T> = {
    options: MultiSelectOption<T>[],
    onItemClick: (value: T, checked: boolean) => Promise<void> | void
    labels?: {
        main?: string,
        noSelection?: string,
        multi?: string,
        noOptions?: string
    }
}

type State = {
    open: boolean
    selectedCount: number
};

export class MultiSelectDropDown<T extends any = string>
    extends React.Component<Props<T>, State> {

    constructor(props: Props<T>) {
        super(props);
        const selectedCount = props.options.filter(o => o.checked).length;
        this.state = {
            open: false,
            selectedCount
        }
    }

    render() {
        const multiSelectOptions = this.props.options.filter(o => o.checked);
        const selectedCount = multiSelectOptions.length;
        let label;
        switch (selectedCount) {
            case 0:
                label = this.props.labels?.noSelection || "All";
                break;
            case 1:
                label = multiSelectOptions[0].label;
                break;
            default:
                label = this.props.labels?.multi || "Multi";
        }

        if (this.props.options.length === 0)
            label = this.props.labels?.noOptions || "No Options";

        const mainLabel = this.props.labels?.main;
        return <div className={`ll_v_c multi-select`}>
            <div
                className={'clickable head'}
                onClick={() => this.setState(s => ({open: !s.open}))}
                style={{position: "relative"}}
            >
                {mainLabel && <div className={"label"}>
                    {mainLabel}
                </div>}
                <div className={'wrapper'}>
                    {this.renderLabel(label)}
                    {this.state.open && <div className={'container'}>
                        {this.getElements()}
                    </div>}
                </div>
            </div>
            {this.state.open && <div className={'overlay'} onClick={() => this.setState({open: false})}/>}
        </div>;
    }

    private renderLabel(label: string) {
        return <div style={{
            border: "solid",
            borderWidth: "1px",
            color: COLORS.neutral_text_dark,
            backgroundColor: this.state.open ? COLORS.very_light_blue : `rgb(244, 247, 247)`,
            borderColor: this.state.open ? COLORS.primary_blue : COLORS.grays_2,
            display: "flex",
            height: "25px"
        }}>
            <div style={{
                width: "130px",
                marginLeft: "5px",
                marginTop: "3px",
                overflow: "hidden"
            }}>{label}</div>
            <div style={{width: 16, height: 16, borderRadius: "100%", backgroundColor: COLORS.very_light_blue, marginTop: "3px", marginRight: "5px"}}>
                {this.state.open ? <img alt={"arrow up"} src={icon_arrow_up} height={16} width={16}
                                        style={{display: "block", margin: "auto"}}/> : ICONS.icon_arrow_down(COLORS.primary_dark_blue, 16)}
            </div>
        </div>;
    }

    private getElements() {
        return this.props.options.map(o => {
            return <ItemRender
                key={String(o.value)}
                {...o}
                onItemClick={() => this.onItemClick(o)}
            />;
        });
    }

    onItemClick = async (o: MultiSelectOption<T>) => {
        await this.props.onItemClick(o.value, !o.checked);
        this.setState(s => {
            const selectedCount = o.checked ? s.selectedCount - 1 : s.selectedCount + 1;
            return {
                ...s,
                selectedCount
            }
        });
    }
}

type MProps<T extends any = string> = {
    label: string,
    value: T,
    checked: boolean,
    count?: number
    onItemClick: () => Promise<void> | void
}
const ItemRender = ({checked, value, label, count, onItemClick}: MProps<any>) => {
    const [loading, setLoading] = useState(false);
    return <div
        style={{alignSelf: "flex-start", color: COLORS.neutral_text_dark, fontSize: 14, padding: "8px", width: "100%"}}
        className={"item"}
        onClick={(e) => {
            e.stopPropagation();

            if (loading)
                return;

            const maybePromise = onItemClick();
            if (maybePromise instanceof Promise) {
                setLoading(true);
                maybePromise.catch(console.error).finally(() => setLoading(false));
            }
        }}
    >
        <TS_Checkbox
            innerNode={(_checked) => loading ? <span className="spinner"></span> : <InnerNode checked={_checked}/>}
            value={value}
            checked={checked}
            label={label}
            disabled={loading}
            buttonClass={loading ? () => "spinner-container" : undefined}
        />
        {count !== undefined && <div style={{marginLeft: "auto", color: COLORS.bluish}}>{count}</div>}
    </div>;
}

